import { Preferences } from "@capacitor/preferences";
import axios from "axios";

const PENDING_IDS_KEY = "altBilling:pendingIds";
const pendingKey = (id) => `altBilling:pending:${id}`;
const BACKOFF_STEPS_MS = [5000, 15000, 60000, 300000];
const MAX_PENDING_AGE_MS = 48 * 60 * 60 * 1000;
const PERMANENT_REJECTION_REASONS = ["already_reported", "product_mismatch"];

const isExpired = (record) =>
    Date.now() - new Date(record.createdAt).getTime() > MAX_PENDING_AGE_MS;

const getStorageItem = async (key) => {
    const { value } = await Preferences.get({ key });
    return value ? JSON.parse(value) : null;
};

const setStorageItem = (key, value) =>
    Preferences.set({ key, value: JSON.stringify(value) });

const getPendingIds = async () => (await getStorageItem(PENDING_IDS_KEY)) || [];

const setPendingIds = (ids) => setStorageItem(PENDING_IDS_KEY, ids);

const addPendingId = async (id) => {
    const ids = await getPendingIds();
    if (!ids.includes(id)) {
        await setPendingIds([...ids, id]);
    }
};

const removePendingId = async (id) => {
    const ids = await getPendingIds();
    await setPendingIds(ids.filter((existingId) => existingId !== id));
};

export const getPendingRecord = (purchaseAttemptId) =>
    getStorageItem(pendingKey(purchaseAttemptId));

export const createPendingRecord = async ({
    purchaseAttemptId,
    productId,
    orderType,
    uniqueIdentifier,
}) => {
    const record = {
        purchaseAttemptId,
        productId,
        orderType,
        uniqueIdentifier,
        createdAt: new Date().toISOString(),
        externalTransactionToken: null,
        tokenCreatedAt: null,
        attempts: 0,
        lastAttemptAt: null,
        lastError: null,
    };

    await setStorageItem(pendingKey(purchaseAttemptId), record);
    await addPendingId(purchaseAttemptId);

    return record;
};

export const storeToken = async (
    purchaseAttemptId,
    externalTransactionToken
) => {
    const record = await getPendingRecord(purchaseAttemptId);
    if (!record) {
        return null;
    }

    const updated = {
        ...record,
        externalTransactionToken,
        tokenCreatedAt: new Date().toISOString(),
    };
    await setStorageItem(pendingKey(purchaseAttemptId), updated);

    return updated;
};

export const clearPendingRecord = async (purchaseAttemptId) => {
    await Preferences.remove({ key: pendingKey(purchaseAttemptId) });
    await removePendingId(purchaseAttemptId);
};

const isDueForRetry = (record) => {
    if (!record.lastAttemptAt) {
        return true;
    }

    const step = Math.min(
        Math.max(record.attempts - 1, 0),
        BACKOFF_STEPS_MS.length - 1
    );
    const waitMs = BACKOFF_STEPS_MS[step];

    return Date.now() - new Date(record.lastAttemptAt).getTime() >= waitMs;
};

export const isRecordDueForRetry = isDueForRetry;

export const recordAttempt = async (purchaseAttemptId) => {
    const record = await getPendingRecord(purchaseAttemptId);
    if (!record) {
        return;
    }

    await setStorageItem(pendingKey(purchaseAttemptId), {
        ...record,
        attempts: record.attempts + 1,
        lastAttemptAt: new Date().toISOString(),
    });
};

export const submitPending = async (purchaseAttemptId) => {
    const record = await getPendingRecord(purchaseAttemptId);
    if (!record || !record.externalTransactionToken) {
        return;
    }

    try {
        await axios.post(route("paywall.google_play.receive_token"), {
            order_type: record.orderType,
            product_code: record.productId,
            unique_identifier: record.uniqueIdentifier,
            external_transaction_token: record.externalTransactionToken,
            token_created_at: record.tokenCreatedAt,
        });

        await clearPendingRecord(purchaseAttemptId);
    } catch (error) {
        if (
            PERMANENT_REJECTION_REASONS.includes(error?.response?.data?.reason)
        ) {
            await clearPendingRecord(purchaseAttemptId);
            return;
        }

        await setStorageItem(pendingKey(purchaseAttemptId), {
            ...record,
            attempts: record.attempts + 1,
            lastAttemptAt: new Date().toISOString(),
            lastError: error?.message || "unknown error",
        });
    }
};

export const sweepPending = async () => {
    const ids = await getPendingIds();

    for (const id of ids) {
        const record = await getPendingRecord(id);
        if (!record) {
            continue;
        }

        if (isExpired(record)) {
            await clearPendingRecord(id);
            continue;
        }

        if (record.externalTransactionToken && isDueForRetry(record)) {
            await submitPending(id);
        }
    }
};

export const findPendingByUniqueIdentifier = async (uniqueIdentifier) => {
    const ids = await getPendingIds();

    for (const id of ids) {
        const record = await getPendingRecord(id);
        if (!record) {
            continue;
        }

        if (isExpired(record)) {
            await clearPendingRecord(id);
            continue;
        }

        if (record.uniqueIdentifier === uniqueIdentifier) {
            return record;
        }
    }

    return null;
};

export const findPendingWithoutToken = async () => {
    const ids = await getPendingIds();

    for (const id of ids) {
        const record = await getPendingRecord(id);
        if (!record) {
            continue;
        }

        if (isExpired(record)) {
            await clearPendingRecord(id);
            continue;
        }

        if (!record.externalTransactionToken) {
            return record;
        }
    }

    return null;
};
